import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PatchWarPlanInput, WarPlanDTO } from '@warplan/shared';
import { api } from './api-client.js';

export const WAR_PLAN_QUERY_KEY = ['war-plan'] as const;

export function useWarPlan() {
  return useQuery({
    queryKey: WAR_PLAN_QUERY_KEY,
    queryFn: () => api.get<WarPlanDTO>('/api/war-plan'),
    staleTime: 60_000,
  });
}

export function useUpdateWarPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PatchWarPlanInput) =>
      api.patch<WarPlanDTO>('/api/war-plan', patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: WAR_PLAN_QUERY_KEY });
      const previous = qc.getQueryData<WarPlanDTO>(WAR_PLAN_QUERY_KEY);
      if (previous) {
        const next: WarPlanDTO = { ...previous };
        if (patch.title !== undefined) next.title = patch.title;
        if (patch.warStyle !== undefined) next.warStyle = patch.warStyle;
        if (patch.opFrequency !== undefined) next.opFrequency = patch.opFrequency;
        if (patch.stockManagement !== undefined) next.stockManagement = patch.stockManagement;
        if (patch.griefingRisk !== undefined) next.griefingRisk = patch.griefingRisk;
        qc.setQueryData<WarPlanDTO>(WAR_PLAN_QUERY_KEY, next);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(WAR_PLAN_QUERY_KEY, ctx.previous);
    },
    onSuccess: (updated) => {
      qc.setQueryData<WarPlanDTO>(WAR_PLAN_QUERY_KEY, updated);
    },
  });
}
